<template>
    <div>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center pb-0">
                <h3 class="font-size-20 font-weight-600">Questions & Answers</h3>

                <v-btn color="primary" @click="openAddFaqModal">
                    <v-icon left>mdi-plus</v-icon>
                    Add New FAQ
                </v-btn>
            </v-card-title>

            <v-card-text>
                <div class="text-center" v-if="loading">
                    <p class="font-size-20 font-weight-600 my-4">
                        Please wait...
                    </p>
                    <v-progress-linear color="cyan" indeterminate rounded height="6"></v-progress-linear>
                    <br />
                </div>
                <div class="faq-items-list" v-else>
                    <!-- If no items -->
                    <div v-if="faqItems.length === 0" class="text-center py-5">
                        <v-icon size="64" color="grey lighten-2">mdi-frequently-asked-questions</v-icon>
                        <p class="mt-3 text-muted">
                            No questions added yet. Click "Add New FAQ" to get started.
                        </p>
                    </div>

                    <!-- DRAGGABLE LIST -->

                    <Draggable tag="div" v-model="faqItems" class="input-options-drag-group"
                        draggable=".input-options-drag" handle=".drag-handle" ghost-class="input-options-ghost"
                        animation="200" group="input-options-drag" v-on:start="drag = true" v-on:end="drag = false"
                        v-on:change="change_drag">
                        <transition-group tag="div" type="transition" :name="!drag ? 'flip-list' : null"
                            v-for="(item, index) in faqItems" :key="'field' + index" class="input-options-drag">
                            <v-col cols="12" :key="`label-${index}`">
                                <v-card class="faq-item-card">
                                    <v-card-text>
                                        <v-row class="mb-2">
                                            <v-col class="pt-1 my-auto" md="2" sm="12">
                                                <label class="font-size-16 font-weight-500">Question {{ index + 1
                                                }}</label>
                                            </v-col>
                                            <v-col class="pt-1" md="8" sm="12">
                                                <div class="question-display">{{ item.question }}</div>
                                            </v-col>
                                            <v-col class="pt-1 d-flex justify-end" md="2" sm="12">
                                                <v-switch true-icon="mdi-checkbox-marked"
                                                    false-icon="mdi-checkbox-outlined" v-model="item.status"
                                                    class="mt-0 mr-2" v-on:click.prevent.stop="activeInactive(item)"
                                                    color="green"></v-switch>
                                                <v-btn min-width="20px" width="20px" color="primary" icon
                                                    @click="editFaqItem(item, index)" class="mr-2">
                                                    <v-icon>mdi-pencil</v-icon>
                                                </v-btn>
                                                <v-btn min-width="20px" width="20px" color="error" icon
                                                    @click="removeFaqItemDialog(item.id)" class="mr-2">
                                                    <v-icon>mdi-delete</v-icon>
                                                </v-btn>
                                                <v-btn min-width="20px" width="20px" icon color="blue darken-4"
                                                    :key="`drag-handle-${index}`" class="cursor-drag drag-handle">
                                                    <v-icon>mdi-drag</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>

                                        <v-row class="mb-2">
                                            <v-col class="pt-1 my-auto" md="2" sm="12">
                                                <label class="font-size-16 font-weight-500">Answer</label>
                                            </v-col>
                                            <v-col class="pt-1" md="10" sm="12">
                                                <div class="answer-display" v-html="item.answer"></div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </transition-group>
                    </Draggable>
                </div>
            </v-card-text>

        </v-card>

        <!-- Add / Update Modal -->
        <AddUpdateFaq :dialog="showFaqModal" :type="type" :type-id="typeId" :id="faqId" :data="faqData"
            @close-dialog="closeFaqModal" @success="successFaq" />

        <!-- Delete Confirmation -->
        <Dialog :dialog="confirmDialog" :dialog-width="500">
            <template #title>Confirmation</template>
            <template #body>
                <p class="font-weight-600 font-size-20 color-custom-blue">
                    Are you sure you want to delete this FAQ item?
                </p>
            </template>
            <template #action>
                <v-btn depressed color="cyan" class="mx-2 white--text" @click="removeFaqItem()" :loading="deleteLoading"
                    :disabled="deleteLoading">
                    Yes
                </v-btn>
                <v-btn depressed class="mx-2" @click="confirmDialog = false" :disabled="deleteLoading">
                    No
                </v-btn>
            </template>
        </Dialog>
    </div>
</template>

<script>
import Draggable from "vuedraggable";
import AddUpdateFaq from "@/view/pages/partials/AddUpdateFaq";
import validationMixin from "@/core/plugins/validation-mixin";
import { GET, POST, PUT, DELETE, QUERY } from "@/core/services/store/request.module";
import { mapActions } from "vuex";
import { map } from "lodash";
import Dialog from "@/view/components/Dialog.vue";

export default {
    name: "FAQ",

    components: { AddUpdateFaq, Dialog, Draggable },

    mixins: [validationMixin],

    props: {
        type: { type: String, default: 'common' },
        typeId: { type: Number, default: 0 }
    },

    data() {
        return {
            faqItems: [],
            showFaqModal: false,
            faqId: null,
            drag: false,
            faqData: {},
            confirmDialog: false,
            removeId: 0,
            loading: true,
            deleteLoading: false
        };
    },

    methods: {
        ...mapActions([GET, POST, PUT, DELETE]),

        change_drag() {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                const updated_order = map(this.faqItems, (row, index) => {
                    return { id: row.id, sort_order: index };
                });
                // this.faqItems = updated_order;
                this.$store.dispatch(PUT, {
                    url: "web-setup/faq-sort-order",
                    data: { items: updated_order }
                });
            }, 300);
        },
        // Load FAQ list
        async loadFaqData() {
            this.loading = true;
            let data = { type: this.type };
            if (this.typeId > 0) data.type_id = this.typeId;

            this.$store.dispatch(QUERY, { url: "web-setup/faq-data", data })
                .then(({ data }) => this.faqItems = data || [])
                .finally(() => this.loading = false);
        },

        // Drag & Drop order update
        onDragEnd() {
            const sorted = this.faqItems.map((faq, index) => ({
                id: faq.id,
                order: index + 1
            }));

            this.$store.dispatch(PUT, {
                url: "web-setup/faq-sort-order",
                data: { items: sorted }
            });
        },

        // Toggle active/inactive
        activeInactive(item) {
            const payload = { ...item, status: item.status ? 1 : 0 };
            this.$store.dispatch(PUT, {
                url: `web-setup/faq-items/${item.id}`,
                data: payload
            }).finally(() => this.loadFaqData());
        },

        openAddFaqModal() {
            this.showFaqModal = true;
        },

        editFaqItem(item) {
            this.faqId = item.id;
            this.faqData = { ...item };
            this.showFaqModal = true;
        },

        closeFaqModal() {
            this.showFaqModal = false;
            this.faqId = null;
            this.faqData = {};
        },

        successFaq() {
            this.closeFaqModal();
            this.loadFaqData();
        },

        removeFaqItemDialog(id) {
            this.removeId = id;
            this.confirmDialog = true;
        },

        removeFaqItem() {
            this.deleteLoading = true
            this.$store.dispatch(DELETE, {
                url: `web-setup/faq-items/${this.removeId}`
            }).then(() => {
                this.loadFaqData();
                this.removeId = 0;
            }).finally(() => {
                this.confirmDialog = false;
                this.deleteLoading = true;
            });
        }
    },

    mounted() {
        this.loadFaqData();
    }
};
</script>

<style scoped>
.drag-handle {
    cursor: move !important;
}
</style>
